import React, { Dispatch, SetStateAction } from 'react';
import { ProviderLocations, SuggestEditModal } from '~/components/Patient';
import { enumValue, formatPhone, handleButtonKeypress } from '~/util';
import { ServicesContext } from '~/services';
import {
    ProviderEntityEnum,
    ProviderType,
    TierEnum,
    getProviderInfo,
    getProviderTier,
} from '~/util/providerUtils';
import { ReferralContext, ReferralContextType } from '~/services/ReferralContext';
import { ProviderSpecialty } from './ProviderSpecialty';
import { ProviderAddress } from './ProviderAddress';

export interface ProviderTileProps {
    provider: ProviderType;
    showLocations?: boolean;
    selectedLocation?: undefined | LocationType;
    setSelectedLocation?: Dispatch<SetStateAction<LocationType>>;
    missingRequiredLocation?: boolean;
    setSuggestedEdit?: (x: boolean) => void;
    planCode?: string;
    selectable?: boolean;
}
export function ProviderTile({
    provider,
    showLocations = false,
    selectedLocation,
    setSelectedLocation,
    missingRequiredLocation,
    setSuggestedEdit,
    planCode,
    selectable = false,
}: ProviderTileProps) {
    const context = React.useContext(ServicesContext);
    const { setSelectedProvider } = React.useContext(ReferralContext) as ReferralContextType;
    const { providerName, providerFax, providerPhone, providerIsContact } =
        getProviderInfo(provider);

    // get default location with distance, or closest location
    const { location, locationPhone, locationFax } = React.useMemo(() => {
        const sortedLocation = provider.locations?.sort((a, b) => {
            const aDistance = a.addresses[0]?.distance;
            const bDistance = b.addresses[0]?.distance;
            if (aDistance && a.is_default) return -1;
            if (bDistance && b.is_default) return 1;
            if (aDistance && bDistance) {
                return aDistance > bDistance ? 1 : -1;
            }
            return bDistance !== undefined && bDistance !== null ? 1 : -1;
        })?.[0];
        return {
            location: sortedLocation,
            locationPhone: sortedLocation?.phones.find(
                (phone) => phone.type === enumValue(['core_app', 'phone_types', 'Practice'])
            ),
            locationFax: sortedLocation?.phones.find(
                (phone) => phone.type === enumValue(['core_app', 'phone_types', 'Practice Fax'])
            ),
        };
    }, [provider.locations]);

    // append note to address tooltip based on available locations
    let addressTooltipNote = '';
    if (provider.locations && provider.locations?.length > 1) {
        if (location?.is_default) {
            addressTooltipNote =
                "This is the provider's default location. More locations are available.";
        } else if (location?.addresses[0].distance) {
            addressTooltipNote =
                "This is the provider's closest location to the patient. More locations are available.";
        }
    }

    const { tierName, tierClasses } = provider.hubTags.reduce(getProviderTier, {
        tier: 0,
        tierName: TierEnum.NULL_TIER,
        tierClasses: '',
    });
    const providerAddress = provider.addresses?.[0];

    const planCodeMatch = provider.hubTags.some((hubTag) => hubTag.name === planCode);
    const providerDisabled = provider.active === false;
    const providerSuspended =
        (provider.entityType === ProviderEntityEnum.ORG && provider.invitation?.active === false) ||
        (provider.entityType === ProviderEntityEnum.PHYSICIAN &&
            provider.organization?.invitation?.active === false);
    let disabledTitle = '';
    if (!planCodeMatch)
        disabledTitle =
            'This provider is not in network for the patient’s plan code through WellMed.';
    else if (providerSuspended)
        disabledTitle =
            'Your LeadingReach organization’s connection with this provider is suspended. For help, please contact your LeadingReach administrator.';
    else if (providerDisabled)
        disabledTitle =
            'The provider you selected is not connected to your LeadingReach network. Please contact PortalSupport@WellMed.net to connect this provider.';

    let tileClass = '';
    if (providerDisabled || !planCodeMatch || providerSuspended) {
        tileClass = 'disabled';
    } else if (selectable) {
        tileClass = 'hoverable';
    }

    function selectProvider() {
        if (setSelectedProvider && !providerDisabled && planCodeMatch && !providerSuspended) {
            setSelectedProvider(provider);
        }
    }

    return (
        <div
            className={`col-12 col-md-6 col-lg-4 provider-tile ${tileClass}`}
            title={disabledTitle}
        >
            <div
                className="card mb-2"
                role="button"
                tabIndex={0}
                onClick={selectProvider}
                onKeyUp={(e) => handleButtonKeypress(e, selectProvider)}
            >
                <div className={`card-header ${tierClasses} d-flex justify-content-between`}>
                    {tierName}
                    <span
                        className="provider-type badge badge-outline-secondary"
                        title={
                            providerIsContact
                                ? 'This provider is not on LeadingReach, so referrals will be faxed.'
                                : 'This provider is on LeadingReach, allowing two-way digital communication.'
                        }
                    >
                        {providerIsContact ? 'Fax' : 'Digital'}
                    </span>
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-12">
                            <div className="h5 text-ellipsis" title={disabledTitle || providerName}>
                                {providerName}
                            </div>
                            <div className="d-flex justify-content-between">
                                <div
                                    className="text-ellipsis me-2"
                                    title={
                                        disabledTitle ||
                                        provider?.orgName ||
                                        provider?.practiceName ||
                                        ''
                                    }
                                >
                                    {provider?.orgName || provider?.practiceName || <>&nbsp;</>}
                                </div>
                                {!showLocations && (
                                    <ProviderAddress
                                        name=""
                                        srName={provider.practiceName}
                                        address={
                                            providerIsContact
                                                ? providerAddress
                                                : location?.addresses[0]
                                        }
                                        phone={providerIsContact ? providerPhone : locationPhone}
                                        fax={providerIsContact ? providerFax : locationFax}
                                        isGenericAddress
                                        tooltipNote={addressTooltipNote}
                                    />
                                )}
                            </div>
                            <div
                                className="text-ellipsis"
                                title={disabledTitle || provider?.npi || ''}
                            >
                                {provider?.npi || <>&nbsp;</>}
                            </div>
                            <ProviderSpecialty specialties={provider?.specialties} />
                            {showLocations && providerFax && (
                                <div>
                                    Fax: {formatPhone(providerFax)}
                                    <i
                                        className="fa-regular fa-question-circle ms-2"
                                        title="Referrals to this provider will be faxed."
                                    />
                                </div>
                            )}
                            {showLocations && providerAddress && (
                                <div>
                                    <ProviderAddress
                                        name={`${providerAddress.municipality}, ${providerAddress.administrative_area}`}
                                        srName={provider.practiceName}
                                        address={providerAddress}
                                        phone={providerPhone}
                                        fax={providerFax}
                                    />
                                </div>
                            )}
                        </div>
                        {missingRequiredLocation && (
                            <div className="text-danger fw-bold">
                                Please select a location to send the referral.
                            </div>
                        )}
                        <ProviderLocations
                            showLocations={showLocations}
                            locations={provider.locations}
                            selectedLocation={selectedLocation!}
                            setSelectedLocation={setSelectedLocation!}
                        />
                        {context.user.hasFeatureToggle('suggested_edits') && showLocations && (
                            <div className="text-end">
                                <SuggestEditModal
                                    provider={provider}
                                    setSuggestedEdit={setSuggestedEdit}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
